import {useCallback, useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {useLocation, useNavigate} from 'react-router-dom';
import {ArrowRight} from 'lucide-react';
import {Button, Dialog, DialogContent, DialogTitle} from '@chekinapp/ui';
import {PAYMENT_PROVIDERS} from '@chekin/core';
import {StatusModalStates} from '@chekin/shared';
import {
  useFetchMultiPayment,
  usePath,
  usePaymentsCart,
  usePaymentSettings,
  usePaymentStageStore,
} from 'hooks';
import {PATHS} from 'routes/constants';
import {APP_PAGES} from 'utils/pages';
import {StripeProvider} from 'context/stripe';
import {useRegisterNavigationGuard} from 'context/navigationGuard';
import {POSTHOG_EVENTS} from 'analytics/posthog/constants';
import {useAnalytics} from 'analytics/posthog';
import PaymentForm from 'components/PaymentForm';
import {AddonPaymentForm} from 'components/AddonPaymentForm';
import {usePaymentFlowStore, type PaymentFlowRequest} from './usePaymentFlowStore';

function PaymentFlowContent({request}: {request: PaymentFlowRequest}) {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const {getFullPath} = usePath();
  const {track} = useAnalytics();
  const close = usePaymentFlowStore(state => state.close);
  const {paymentProvider} = usePaymentSettings();
  const paymentStage = usePaymentStageStore(state => state.stage);
  const isConfirmingPayment = paymentStage === StatusModalStates.LOADING;

  useRegisterNavigationGuard({
    when: isConfirmingPayment,
    title: t('payment_still_confirming_title'),
    description: t('payment_still_confirming_description'),
    confirmLabel: t('leave_anyway'),
    cancelLabel: t('stay_on_this_page'),
  });

  useEffect(() => {
    if (!isConfirmingPayment) return;
    const warnBeforeUnload = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener('beforeunload', warnBeforeUnload);
    return () => window.removeEventListener('beforeunload', warnBeforeUnload);
  }, [isConfirmingPayment]);

  const {paymentsCart, isPaymentsCartLoading} = usePaymentsCart({
    defaultDeferredPayments: request.pay_later_payments,
  });
  const {data: multiPayment, isInitialLoading: isMultiPaymentLoading} =
    useFetchMultiPayment({id: request.paymentId});

  useEffect(() => {
    if (
      paymentProvider === PAYMENT_PROVIDERS.addon &&
      !isMultiPaymentLoading &&
      !multiPayment?.id
    ) {
      close();
    }
  }, [close, isMultiPaymentLoading, multiPayment?.id, paymentProvider]);

  const handleSuccess = useCallback(() => {
    track(POSTHOG_EVENTS.PAYMENT_COMPLETED, {
      paymentProvider,
      total_amount_to_pay: paymentsCart?.total_amount_to_pay || 0,
    });
  }, [paymentProvider, paymentsCart?.total_amount_to_pay, track]);

  const handleReceiptsReady = useCallback(() => {
    close();
    navigate(getFullPath(PATHS.payments.ordersHistory), {state: {fromPayment: true}});
  }, [close, getFullPath, navigate]);

  const isFromCheckout = request.from === APP_PAGES.checkout;
  const isRateYourStayNext = Boolean(
    request.redirectAfterPaidPath?.includes(PATHS.checkout.rateYourStay),
  );
  const successActions = isFromCheckout ? (
    <Button
      onClick={() => {
        close();
        navigate(request.redirectAfterPaidPath ?? getFullPath(PATHS.home));
      }}
      className="w-full gap-2 [&_svg]:size-4"
    >
      {isRateYourStayNext
        ? t('continue_rate_your_stay', {ns: 'check-out'})
        : t('continue', {ns: 'check-out'})}
      <ArrowRight />
    </Button>
  ) : undefined;

  if (paymentProvider === PAYMENT_PROVIDERS.stripe) {
    return (
      <PaymentForm
        paymentsCart={paymentsCart}
        isLoading={isPaymentsCartLoading}
        onSuccess={handleSuccess}
        onReceiptsReady={handleReceiptsReady}
        successActions={successActions}
      />
    );
  }

  return (
    <AddonPaymentForm
      paymentsCart={paymentsCart}
      isLoading={isPaymentsCartLoading || isMultiPaymentLoading}
      multiPayment={multiPayment}
      onSuccess={handleSuccess}
      onReceiptsReady={handleReceiptsReady}
      onUnavailable={close}
      successActions={successActions}
    />
  );
}

function PaymentFlowModal() {
  const {t} = useTranslation();
  const {pathname} = useLocation();
  const request = usePaymentFlowStore(state => state.request);
  const close = usePaymentFlowStore(state => state.close);

  useEffect(() => close, [close, pathname]);
  const paymentStage = usePaymentStageStore(state => state.stage);
  const isPaying = paymentStage !== StatusModalStates.IDLE;

  return (
    <Dialog
      open={Boolean(request)}
      onOpenChange={open => {
        if (!open && !isPaying) close();
      }}
    >
      <DialogContent
        showCloseButton={!isPaying}
        aria-describedby={undefined}
        onEscapeKeyDown={event => isPaying && event.preventDefault()}
        onInteractOutside={event => isPaying && event.preventDefault()}
        overlayClassName="py-0 lg:pb-[19px] lg:pt-[20px]"
        className="my-0 h-[var(--app-dvh,100dvh)] w-full max-w-none gap-0 overflow-hidden rounded-none border-0 p-0 sm:max-w-none lg:my-auto lg:h-[min(90vh,620px)] lg:max-w-[760px] lg:rounded-[22px]"
      >
        <DialogTitle className="sr-only">{t('pay_flow.dialog_title')}</DialogTitle>
        {request && (
          <StripeProvider>
            <PaymentFlowContent request={request} />
          </StripeProvider>
        )}
      </DialogContent>
    </Dialog>
  );
}

export {PaymentFlowModal};
